/* Artemis - PDP interactivity. Alpine components register on alpine:init.
   Covers: standard variant picker, size/width/toe quantity matrix (JSON items[] add),
   imprint configurator (FormData multipart add - file path and items[] path are mutually
   exclusive), back-in-stock waitlist, and a shared cart-add helper. */
(function () {
  /* shared cart add lives in artemis.js (window.aAddToCart), loaded before this file. */
  var addToCart = function (body) { return window.aAddToCart(body); };

  /* ---- Subscribe & Save: plan state shared by BOTH buy components ----
     71 of the 114 products carrying selling plans are sized, so this has to work identically in the
     size matrix and in the standard buy box. Mixed in rather than written twice: the is-custom gate
     is the standing lesson on this project about what four copies of one rule cost.

     planId === null means a one-time purchase, which is the default. Money comes from the variant's
     own selling_plan_allocations (Shopify's arithmetic), never from ours; the plan percentage is a
     fallback so the control still states a number for a variant whose allocation is missing.

     Mixed in with Object.assign(literal, subMixin(data)) so the host's own getters survive: the
     mixin adds only plain properties and methods, and collides with none of them. */
  var subMixin = function (data) {
    return {
      plans: (data && data.plans) || [],
      planId: (data && data.planId) ? Number(data.planId) : null,
      subAllocs(v) { return (v && v.sp) || []; },
      // price of one unit of `v` under plan `id` (or one-time when id is falsy)
      subPriceFor(v, id) {
        if (!v) return 0;
        if (!id) return v.price;
        var want = String(id);
        var a = this.subAllocs(v).filter(function (x) { return String(x.id) === want; })[0];
        if (a) return a.p;
        var p = this.plans.filter(function (x) { return String(x.id) === want; })[0];
        return (p && p.pct) ? Math.round(v.price * (1 - p.pct / 100)) : v.price;
      },
      // NOTE: subRef() is deliberately NOT defined here. Each host defines it (the standard box has
      // one resolved variant, the matrix has none until a size is picked), and a default here would
      // OVERWRITE the host's version, because the mixin is the source of the Object.assign.
      subOne() { var v = this.subRef && this.subRef(); return v ? v.price : 0; },
      subPrice(id) { return this.subPriceFor(this.subRef && this.subRef(), id); },
      subPct(id) {
        var one = this.subOne();
        if (!one || !id) return 0;
        return Math.round((one - this.subPrice(id)) / one * 100);
      },
      // the best saving on offer, for the badge beside the heading
      subBest() {
        var self = this;
        return this.plans.reduce(function (m, p) { return Math.max(m, self.subPct(p.id)); }, 0);
      },
      subSet(id) {
        this.planId = id ? Number(id) : null;
        // keep the choice shareable and refreshable, the same way the variant selection is
        try {
          var u = new URL(window.location.href);
          if (this.planId) u.searchParams.set('selling_plan', this.planId);
          else u.searchParams.delete('selling_plan');
          window.history.replaceState(null, '', u);
        } catch (e) {}
      },
      // A variant that does not offer the chosen plan must not keep it, or the add 422s with a
      // message no buyer can act on. Falls back to that variant's first plan, then to one-time.
      subReconcile() {
        if (!this.planId) return;
        var v = this.subRef && this.subRef();
        if (!v) return;
        var allocs = this.subAllocs(v);
        if (!allocs.length) { this.planId = null; return; }
        var want = String(this.planId);
        if (!allocs.some(function (a) { return String(a.id) === want; })) this.planId = Number(allocs[0].id);
      },
      // one place that decides what goes on the wire, so no add path can forget it
      subLine(id, qty) {
        var line = { id: id, quantity: qty };
        if (this.planId) line.selling_plan = this.planId;
        return line;
      }
    };
  };

  document.addEventListener('alpine:init', function () {

    /* ---- standard variant picker (color/size dropdowns or single variant) ---- */
    Alpine.data('aProductForm', function (data) {
      return Object.assign({
        variants: data.variants,
        options: data.options,        // array of {name, position, values}
        selected: data.initial,       // array of selected option values by position
        current: null,
        qty: 1,
        moqMin: (data.moqMin && data.moqMin > 1) ? data.moqMin : 1,   // minimum for low-cost items, else 1
        moqCeiling: data.moqCeiling || 0,                             // unit-price cents below which the min applies
        wantsFewer: false,   // reveals the "buy fewer on Amazon" escape hatch on a MOQ item
        adding: false,
        slow: false,   // true only once an add has run past 150ms (drives the "Adding..." label)
        // the Subscribe & Save control prices the SELECTED variant
        subRef() { return this.current; },
        init() { this.resolve(true); },
        resolve(initial) {
          this.current = this.variants.find((v) =>
            this.selected.every((val, i) => v['option' + (i + 1)] === val)) || null;
          // a plan the newly selected variant does not offer cannot survive the switch
          this.subReconcile();
          // initial=true tags the hydration-time dispatch so the headline price keeps the richer
          // server render (compare-at, save pill, "Starting at") until a REAL user selection.
          // missing=true means the chosen options map to no variant at all (e.g. Orange + 12);
          // aPdpInfo uses it to show a neutral "not available" status instead of reviving the
          // server's stale "In stock." + price, which contradicted the buy button.
          this.$dispatch('variant:change', { variant: this.current, initial: !!initial, missing: this.current === null });
          // window event (the gallery is a sibling root, so a bubbling $dispatch can't reach it):
          // tell it which variant is selected so it can slide to that variant's photo.
          // NOTE: unconditional on purpose; the load-time dispatch is what resolves ?variant
          // deep links to the right gallery image.
          window.dispatchEvent(new CustomEvent('variant-image', { detail: { id: this.current ? this.current.id : null } }));
          // keep the URL shareable/refreshable: real selections sync ?variant (never on load,
          // so landing URLs stay untouched)
          if (!initial && this.current) {
            try {
              var u = new URL(window.location.href);
              u.searchParams.set('variant', this.current.id);
              window.history.replaceState(null, '', u);
            } catch (e) {}
          }
          // MOQ floor: a low-cost variant cannot be added below the minimum, so start (and re-sync on a
          // variant switch) at the floor rather than at 1. Only ever raises qty, never lowers a choice.
          if (this.minQty > 1 && !(parseInt(this.qty) >= this.minQty)) this.qty = this.minQty;
        },
        get available() { return this.current && this.current.available; },
        // selection is complete but no variant matches (e.g. color+size combo that does not exist)
        get comboMissing() { return this.current === null; },
        // state of a candidate value at option index i, given the other currently-selected options:
        // 'missing' (no such variant), 'soldout' (exists, unavailable), or 'ok'
        valueState(i, val) {
          var sel = this.selected.slice();
          sel[i] = val;
          var matches = this.variants.filter((v) => sel.every((s, k) => v['option' + (k + 1)] === s));
          if (!matches.length) return 'missing';
          return matches.some((v) => v.available) ? 'ok' : 'soldout';
        },
        // qty-aware: the button states the true amount being added, matching the size matrix.
        // Under a subscription it states the SUBSCRIPTION price, because that is what gets charged.
        get priceText() {
          if (!this.current) return '';
          var q = parseInt(this.qty) > 0 ? parseInt(this.qty) : 1;
          return this.money(this.subPriceFor(this.current, this.planId) * q);
        },
        money(cents) { return window.aMoney(cents); },
        // on-hand cap for the selected variant when Shopify tracks it and denies oversell; null when
        // untracked or continue-selling (so nothing is clamped on made-to-order / drop-ship variants)
        get stockCap() {
          var c = this.current;
          return (c && c.track === 'shopify' && c.policy === 'deny') ? c.inv : null;
        },
        // the qty field and preset chips respect the cap so the buyer never keys 100 on a 12-unit item
        // and eats a raw 422 after the click
        get maxQty() { var cap = this.stockCap; return (cap != null && cap > 0) ? cap : null; },
        // MOQ floor for the SELECTED variant: low-cost variants (unit price under moqCeiling cents) are
        // sold only in minimums; every other variant/product is 1.
        get minQty() {
          if (this.moqMin <= 1 || !this.moqCeiling) return 1;
          return (this.current && this.current.price < this.moqCeiling) ? this.moqMin : 1;
        },
        inc() {
          var n = (parseInt(this.qty) || this.minQty) + 1;
          var m = this.maxQty;
          this.qty = (m != null && n > m) ? m : n;
        },
        // minus at the floor is the buyer telling us they want fewer than the pack: surface the Amazon
        // escape hatch instead of just swallowing the click.
        dec() {
          var n = (parseInt(this.qty) || this.minQty) - 1;
          if (n < this.minQty) { this.wantsFewer = true; this.qty = this.minQty; return; }
          this.qty = n;
        },
        // typed a number below the floor (fires on change/blur, not per keystroke): same signal.
        clampMin() {
          var n = parseInt(this.qty);
          if (!(n > 0)) { this.qty = this.minQty; return; }
          if (n < this.minQty) { this.wantsFewer = true; this.qty = this.minQty; }
        },
        async add() {
          if (!this.current || !this.available) return;
          var qty = parseInt(this.qty) > 0 ? parseInt(this.qty) : 1;
          if (this.minQty > 1 && qty < this.minQty) qty = this.minQty;   // never add below the MOQ floor
          var cap = this.stockCap;
          if (cap != null && cap > 0 && qty > cap) {
            qty = cap;
            if (window.aToast) window.aToast(window.aT('stock_clamped', { n: cap }), 'warn');
          }
          this.qty = qty; // re-sync a cleared/invalid/over-cap field
          this.adding = true;
          // :disabled stays on `adding` (instant double-submit guard); the label only swaps to
          // "Adding..." once the write is slow enough to notice (>150ms), so a fast add never
          // flickers the label or reflows the button width. Same threshold the cart drawer uses.
          var st = setTimeout(() => { this.slow = true; }, 150);
          try { await addToCart({ items: [this.subLine(this.current.id, qty)] }); }
          catch (e) {} finally { clearTimeout(st); this.slow = false; this.adding = false; }
        }
      }, subMixin(data));
    });

    /* ---- PDP headline price + availability, reactive to variant selection ----
       The price/availability render outside the buy-standard scope; this catches the bubbling
       variant:change event so they never go stale or contradict the buy button. */
    Alpine.data('aPdpInfo', function (data) {
      return {
        cv: null,
        // touched flips on the first REAL selection; missing is true when that selection maps to
        // no variant. Together they let the template hide the stale server "In stock." + price
        // once the buyer has actively chosen an unavailable combination.
        touched: false,
        missing: false,
        inStock: (data && data.inStock) || '',
        oos: (data && data.oos) || '',
        // the SKU rendered by the server, i.e. the one for the variant the page loaded on
        serverSku: (data && data.sku) || '',
        skuPrefix: (data && data.skuPrefix) || '',
        mpnPrefix: (data && data.mpnPrefix) || '',
        serverMpn: (data && data.mpn) || '',
        // the hydration-time dispatch is ignored so the server-rendered price block (compare-at,
        // save pill, "Starting at") persists until the buyer actually changes a selection
        onVariant(v, initial, missing) {
          if (initial) return;
          this.touched = true;
          this.cv = v || null;
          this.missing = !!missing;
          this.syncSku();
        },
        // Keep the identifier line in step with the selection. Everything else on this page already
        // followed the buyer (price, availability, the ?variant URL); the SKU did not, so choosing
        // Black left the GREEN sku on screen. That is the one field this line exists for: it is
        // surfaced above the fold precisely so B2B re-order desks can verify against it, and a
        // confidently wrong SKU is worse than no SKU.
        //
        // Done imperatively rather than with x-text because the identifier line lives in the shared
        // product header, which renders INSIDE this component on the standard PDP but outside it on
        // the custom one. A binding in shared markup would throw on the layout where this component
        // does not exist. The MPN moves with it now: an earlier note here claimed ols.mpn was
        // product-level on this store and therefore could not vary, which was wrong. Every variant
        // carries its own (RD1019-S, RD1019-M), so a static MPN was the same defect as the static
        // SKU, one field to the right.
        syncSku() {
          var el = this.$root && this.$root.querySelector('.a-pdp__sku-val');
          if (el) {
            var sku = (this.cv && this.cv.sku) || this.serverSku;
            if (sku) el.textContent = this.skuPrefix + sku;
          }
          var mel = this.$root && this.$root.querySelector('.a-pdp__mpn-val');
          if (mel) {
            var mpn = (this.cv && this.cv.mpn) || this.serverMpn;
            if (mpn) mel.textContent = this.mpnPrefix + mpn;
          }
          // The spec table repeats both identifiers lower down the page. It is queried off document
          // rather than $root because it sits outside this component, and it takes the bare value
          // because its own <dt> already says SKU or MPN. Without this the standard PDP had the same
          // split Patrick found on the custom one: buy box correct, spec table still on variant one.
          var ss = document.querySelector('.a-pdp__spec-sku');
          if (ss) {
            var sku2 = (this.cv && this.cv.sku) || this.serverSku;
            if (sku2) ss.textContent = sku2;
          }
          var sm = document.querySelector('.a-pdp__spec-mpn');
          if (sm) {
            var mpn2 = (this.cv && this.cv.mpn) || this.serverMpn;
            if (mpn2) sm.textContent = mpn2;
          }
        },
        money(c) { return window.aMoney(c); }
      };
    });

    /* ---- size / width / toe quantity matrix (multi-line JSON add) ---- */
    Alpine.data('aMatrix', function (data) {
      return Object.assign({
        variants: data.variants,
        rows: data.rows,        // size axis values
        cols: data.cols,        // width/toe axis values, or [''] if 1-D
        // tri-axis mode (Color/Size/Width boots): sels holds the first axis's values, rendered as
        // swatch chips in the snippet; rows/cols are then option2/option3. Before this, the third
        // axis was dropped and the matrix sold whichever width came first (Website Issues 2).
        sels: data.sels || [],
        productId: data.productId,
        qtys: {},               // variantId to qty
        moqMin: (data.moqMin && data.moqMin > 1) ? data.moqMin : 0,   // minimum units for a MOQ (low-cost) item, else 0
        adding: false,
        slow: false,   // gates the size-run "Adding..." label behind a 150ms threshold (no fast-add flicker)
        runOpen: false, // size-run table collapsed by default; crew buyers open it
        // single-pair fast path: most sized orders are one size; those buyers should not have
        // to parse a wholesale grid. The matrix stays right below for size-run orders.
        oneSize: '',
        oneCol: '',
        oneSel: (data.sels && data.sels.length) ? data.sels[0] : '',
        get tri() { return this.sels.length > 0; },
        // variant option accessors for the mapped axes
        // _vSel(v) { return v.option1; },
        // _vRow(v) { return this.tri ? v.option2 : v.option1; },
        // _vCol(v) { return this.tri ? v.option3 : v.option2; },
        // _inSel(v) { return !this.tri || v.option1 === this.oneSel; },
        selIdx: data.selIdx != null ? data.selIdx : ((data.sels && data.sels.length) ? 0 : -1),
        rowIdx: data.rowIdx != null ? data.rowIdx : ((data.sels && data.sels.length) ? 1 : 0),
        colIdx: data.colIdx != null ? data.colIdx : ((data.sels && data.sels.length) ? 2 : 1),
        _opt(v, i) { return i === 0 ? v.option1 : i === 1 ? v.option2 : i === 2 ? v.option3 : undefined; },
        _vSel(v) { return this._opt(v, this.selIdx); },
        _vRow(v) { return this._opt(v, this.rowIdx); },
        _vCol(v) { return this._opt(v, this.colIdx); },
        _inSel(v) { return !this.tri || this._vSel(v) === this.oneSel; },

        init() {
          // Restore a size-run draft (the 20.4% multi-size flow is the costliest abandonment surface;
          // a tapped Similar item, an opened spec PDF, or a refresh used to wipe every keyed cell).
          this._skey = 'sg_matrix_' + (this.productId || 'x');
          try { var s = JSON.parse(sessionStorage.getItem(this._skey) || 'null'); if (s && typeof s === 'object') this.qtys = s; } catch (e) {}
          // Open the run by default when it is the primary on-store path (a restored draft, or a MOQ
          // item whose single-pair fast path is replaced by the Amazon escape so the run is the only
          // way to buy on-store). Otherwise it stays collapsed for the ~80% single-pair buyer.
          if (this.moqMin > 0 || Object.values(this.qtys).some(function(v) { return parseInt(v) > 0; })) this.runOpen = true;
          // Announce the fast-path selection, the way the standard PDP's aProductForm does.
          // Reported 2026-07-31: "This system does not change the product image to the correct
          // variant image." True on SIZED products only, which is why it did not reproduce on a hard
          // hat: the gallery listens for a `variant-image` window event, and the matrix was the one
          // buy surface that never fired it. Picking Moss on a boot left a Pink boot on screen.
          // variant:change goes with it, so the SKU, price and availability lines follow the
          // selection here too; that fix landed on 2026-07-31 for the standard PDP and this surface
          // was never wired to it.
          this.$watch('oneSize', () => this.announce());
          this.$watch('oneCol', () => this.announce());
          // colour first, size second: switching the chip must swap the photo immediately, exactly
          // like the size fallback below (P11's rule: the gallery follows the colourway).
          this.$watch('oneSel', () => this.announce());
        },
        announce() {
          var v = this.oneVariant;
          // Buyers pick the colourway first and the size second, and until both are set there is no
          // variant to announce. There IS a colourway to look at, though, so the IMAGE falls back to
          // the first variant carrying that first option: picking Moss shows a Moss boot immediately
          // instead of waiting for a size that the buyer may well be choosing BECAUSE of the photo.
          // Only the image falls back. variant:change still carries the real (possibly null)
          // variant, so price, SKU and availability never describe a variant nobody selected.
          var img = v
            || (this.tri && this.oneSel ? this.variants.find((x) => x.option1 === this.oneSel) : null)
            || (!this.tri && this.oneSize ? this.variants.find((x) => x.option1 === this.oneSize) : null)
            || (this.tri && this.oneSel ? this.variants.find((x) => this._vSel(x) === this.oneSel) : null)
            || (!this.tri && this.oneSize ? this.variants.find((x) => this._vRow(x) === this.oneSize) : null);
            
          window.dispatchEvent(new CustomEvent('variant-image', { detail: { id: img ? img.id : null } }));
          this.$dispatch('variant:change', { variant: v, initial: false, missing: this.oneMissing });
        },
        persist() { try { sessionStorage.setItem(this._skey, JSON.stringify(this.qtys)); } catch (e) {} },
        cell(rowVal, colVal) {
          return this.variants.find((v) => this._inSel(v)
            && this._vRow(v) === rowVal && (colVal === '' || this._vCol(v) === colVal));
        },
        // availability of a size given the current width pick (or across all widths when none chosen):
        // 'missing' (no such variant), 'soldout' (exists, all unavailable), or 'ok'
        sizeState(val) {
          var col = this.cols[0] === '' ? '' : this.oneCol;
          var m = this.variants.filter((v) => this._inSel(v)
            && this._vRow(v) === val && (!col || this._vCol(v) === col));
          if (!m.length) return 'missing';
          return m.some((v) => v.available) ? 'ok' : 'soldout';
        },
        // availability of a width given the current size pick
        colState(val) {
          if (!this.oneSize) return 'ok';
          var m = this.variants.filter((v) => this._inSel(v)
            && this._vRow(v) === this.oneSize && this._vCol(v) === val);
          if (!m.length) return 'missing';
          return m.some((v) => v.available) ? 'ok' : 'soldout';
        },
        get oneVariant() {
          if (!this.oneSize) return null;
          var col = this.cols[0] === '' ? '' : this.oneCol;
          if (this.cols[0] !== '' && !col) return null;
          return this.cell(this.oneSize, col) || null;
        },
        // both selects are set but that size+width pair does not exist as a variant
        get oneMissing() {
          if (!this.oneSize) return false;
          if (this.cols[0] !== '' && !this.oneCol) return false;
          return this.oneVariant === null;
        },
        // Subscribe & Save prices the size the buyer has picked; before they pick one it describes
        // the first variant, which is safe here because a selling plan group covers the whole
        // product, so every size carries the same plans at the same percentage.
        subRef() { return this.oneVariant || this.variants[0] || null; },
        async addOne() {
          var v = this.oneVariant;
          if (!v || !v.available || this.adding) return;
          this.adding = true;
          try { await addToCart({ items: [this.subLine(v.id, 1)] }); }
          catch (e) {} finally { this.adding = false; }
        },
        get totalUnits() { return Object.values(this.qtys).reduce((a, b) => a + (parseInt(b) || 0), 0); },
        // MOQ run gate: a low-cost item is sold only in minimums, so a run under moqMin units cannot be
        // added on-store (the buyer is pointed at Amazon instead). moqMin is 0 for non-MOQ products.
        get belowMin() { return this.moqMin > 0 && this.totalUnits < this.moqMin; },
        get totalPrice() {
          let c = 0;
          for (const id in this.qtys) {
            const v = this.variants.find((x) => x.id == id);
            // under a subscription the run total has to be the subscription total, or the button
            // promises one number and the cart charges another
            if (v) c += this.subPriceFor(v, this.planId) * (parseInt(this.qtys[id]) || 0);
          }
          return window.aMoney(c);
        },
        label(v) { return v ? [v.option1, v.option2, v.option3].filter(Boolean).join(' / ') : ''; },
        money(c) { return window.aMoney(c); },
        // open the shared back-in-stock panel for a specific out-of-stock size
        notify(variantId) {
          var v = this.variants.find((x) => x.id == variantId);
          window.dispatchEvent(new CustomEvent('bis:open', { detail: { variant: variantId, label: this.label(v) } }));
        },
        async add() {
          // Clamp each line to on-hand stock for tracked deny-policy variants. cart/add.js is atomic:
          // one over-stock line would fail the whole multi-size add, so clamp before posting.
          var clamped = [];
          const items = Object.entries(this.qtys)
            .map(([id, q]) => {
              var qty = parseInt(q) || 0;
              var v = this.variants.find((x) => x.id == id);
              if (v && v.track === 'shopify' && v.policy === 'deny' && qty > v.inv) {
                clamped.push(this.label(v) + ' (' + v.inv + ')');
                qty = v.inv;
              }
              return this.subLine(parseInt(id), qty);
            })
            .filter((it) => it.quantity > 0);
          if (!items.length) { if (window.aToast) window.aToast(window.aT('matrix_need_qty'), 'err'); return; }
          // never post a run under the store minimum, even if the disabled button is bypassed (keyboard/programmatic)
          if (this.belowMin) { if (window.aToast) window.aToast(window.aT('moq_run_toast', { n: this.moqMin }), 'err'); return; }
          this.adding = true;
          var st = setTimeout(() => { this.slow = true; }, 150);
          try {
            await addToCart({ items });
            this.qtys = {};
            try { sessionStorage.removeItem(this._skey); } catch (e) {}
            if (clamped.length && window.aToast) window.aToast(window.aT('matrix_clamped', { items: clamped.join(', ') }), 'warn');
          } catch (e) {} finally { clearTimeout(st); this.slow = false; this.adding = false; }
        }
      }, subMixin(data));
    });

    /* ---- imprint configurator (FormData multipart; file + items[] are mutually exclusive) ---- */
    Alpine.data('aConfigurator', function (data) {
      return {
        variants: data.variants,
        shellColors: data.shellColors,   // option1 values
        logoTiers: data.logoTiers,       // option2 values (1-5 Color Logo)
        fields: data.fields,             // personalization JSON schema
        customFee: data.customFee,       // cents
        feeLimit: data.feeLimit,         // qty at/above which setup fee waived
        shell: data.shellColors[0] || null,
        tier: data.logoTiers[0] || null,
        qty: 1,
        files: {}, fileNames: {}, fileError: '', lowRes: false,
        preview: null,                   // object URL of the uploaded logo for the live preview
        props: {},                       // personalization field to value
        step: 1,
        adding: false,
        // live-preview placement maps the "Logo Placement" field to a position on the product
        get placementClass() {
          var p = '';
          for (var k in this.props) { if (/placement/i.test(k) && this.props[k]) { p = (this.props[k] + '').toLowerCase(); break; } }
          if (p.indexOf('back') > -1) return 'is-back';
          if (p.indexOf('left') > -1) return 'is-left';
          if (p.indexOf('right') > -1) return 'is-right';
          return 'is-front';
        },
        maxBytes: 20 * 1024 * 1024,
        accept: ['image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf', 'application/postscript'],
        // parse "Label|index" list values; show Label, store Label
        listOptions(field) {
          return (field.List_Values || '').split(',').map(function (s) {
            var parts = s.split('|'); return { label: parts[0].trim(), value: parts[0].trim() };
          }).filter(function (o) { return o.label; });
        },
        get primaryFileSet() { return Object.keys(this.files).length > 0; },
        get current() {
          return this.variants.find((v) => v.option1 === this.shell && (this.logoTiers.length ? v.option2 === this.tier : true)) || null;
        },
        get unitPrice() { return this.current ? this.current.price : 0; },
        get base() { return this.unitPrice * this.qty; },
        get feeWaived() { return this.feeLimit > 0 && this.qty >= this.feeLimit; },
        get setupFee() { return this.feeWaived ? 0 : this.customFee; },
        get total() { return this.base + this.setupFee; },
        money(c) { return window.aMoney(c); },
        // radiogroup keyboard nav for the color + logo-tier swatches
        shellKey(e) { this.shell = this._roveKey(e, this.shellColors, this.shell); },
        tierKey(e) { this.tier = this._roveKey(e, this.logoTiers, this.tier); },
        _roveKey(e, list, cur) {
          var i = list.indexOf(cur), n = list.length;
          if (!n) return cur;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { i = (i + 1) % n; }
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { i = (i - 1 + n) % n; }
          else if (e.key === 'Home') { i = 0; }
          else if (e.key === 'End') { i = n - 1; }
          else { return cur; }
          e.preventDefault();
          var els = e.currentTarget.querySelectorAll('[role=radio]');
          if (els[i]) els[i].focus();
          return list[i];
        },
        get canAdd() {
          if (!this.current || !this.current.available) return false;
          // brief: add disabled until artwork uploaded AND color count chosen
          if (this.logoTiers.length && !this.tier) return false;
          if (!this.primaryFileSet) return false;
          // require any List field marked Required to be set
          return (this.fields || []).every((f) =>
            f.DISPLAY_TYPE === 'File Upload' || f.Required !== 'true' || (this.props[f.DISPLAY_NAME] && this.props[f.DISPLAY_NAME] !== ''));
        },
        // name the true next blocker in FORM order (placement, finish, artwork...) so the hint is airtight
        get hintText() {
          if (!this.current || !this.current.available) return 'This option is unavailable.';
          if (this.logoTiers.length && !this.tier) return 'Choose your logo colors to continue.';
          var fields = this.fields || [];
          for (var i = 0; i < fields.length; i++) {
            var f = fields[i];
            if (f.DISPLAY_TYPE === 'File Upload') {
              if (!this.primaryFileSet) return 'Upload your artwork to continue.';
            } else if (f.Required === 'true' && !(this.props[f.DISPLAY_NAME] && this.props[f.DISPLAY_NAME] !== '')) {
              return 'Choose ' + (f.DISPLAY_NAME || '').replace(/\*+$/, '').trim().toLowerCase() + ' to continue.';
            }
          }
          return '';
        },
        onFile(fieldName, e) {
          this.fileError = ''; this.lowRes = false;
          const f = e.target.files[0];
          if (!f) { delete this.files[fieldName]; delete this.fileNames[fieldName]; return; }
          if (f.size > this.maxBytes) { this.fileError = 'File is over 20MB.'; return; }
          if (this.accept.indexOf(f.type) === -1 && !/\.(png|jpe?g|svg|pdf|eps|ai)$/i.test(f.name)) {
            this.fileError = 'Use PNG, JPG, SVG, PDF, or EPS.'; return;
          }
          this.files[fieldName] = f; this.fileNames[fieldName] = f.name;
          // live preview for raster + svg logos (the conversion moment)
          if (/^image\/(png|jpe?g|svg\+xml)$/.test(f.type) || /\.svg$/i.test(f.name)) {
            if (this.preview) { try { URL.revokeObjectURL(this.preview); } catch (e) {} }
            this.preview = URL.createObjectURL(f);
          }
          if (/^image\/(png|jpe?g)$/.test(f.type)) {
            const img = new Image(); img.onload = () => { if (img.naturalWidth < 600) this.lowRes = true; };
            img.src = this.preview || URL.createObjectURL(f);
          }
        },
        async add() {
          if (!this.canAdd) return;
          var qty = parseInt(this.qty) > 0 ? parseInt(this.qty) : 1;
          this.qty = qty; // re-sync a cleared/invalid field
          this.adding = true;
          const fd = new FormData();
          fd.append('id', this.current.id);
          fd.append('quantity', qty);
          for (const k in this.props) { if (this.props[k]) fd.append('properties[' + k + ']', this.props[k]); }
          if (this.tier) fd.append('properties[Logo colors]', this.tier);
          // Display-only fee note (charging the setup fee is an operator/backend mechanism - flagged).
          if (this.setupFee > 0) fd.append('properties[Setup fee]', this.money(this.setupFee));
          for (const k in this.files) { fd.append('properties[' + k + ']', this.files[k]); }
          try { await addToCart(fd); } catch (e) {} finally { this.adding = false; }
        }
      };
    });

    /* ---- custom PDP image inspection: a lightbox for the configurator stage + media thumbs ----
       The custom branch renders no gallery, so the Solid-drawn stage photo and the product's other
       media were unreachable (a 999+ unit run got less inspection than a $12 stock hat). This opens
       a full-screen a-zoom on the CURRENT stage image (read at click time, upscaled to 1600) or on a
       thumb's 1600px url, without touching Solid internals. */
    Alpine.data('aCustomZoom', function () {
      return {
        zoom: false, src: '',
        open(url) { if (!url) return; this.src = url; this.zoom = true; },
        onStage(e) {
          var t = e.target;
          if (t && t.classList && t.classList.contains('sgc__base')) {
            this.open((t.currentSrc || t.src || '').replace(/width=\d+/, 'width=1600'));
          }
        }
      };
    });

    /* ---- back-in-stock waitlist (Klaviyo onsite subscribe) ---- */
    Alpine.data('aBackInStock', function (data) {
      return {
        open: false, email: '', sent: false, error: '', sending: false,
        variantId: data.variantId, label: '',
        init() {
          // Follow the buyer's actual selection on a standard PDP. aProductForm.resolve() dispatches
          // 'variant-image' on every selection (and on load); track its id so a shopper who lands on
          // an in-stock variant, then switches to a sold-out one and subscribes, is waitlisted for the
          // variant they are actually looking at, not the page-load variant baked in at render. Do NOT
          // open the panel here (buy-standard reveals it via x-show when the selection is unavailable);
          // this only fires on standard PDPs, since sized PDPs have no aProductForm to dispatch it.
          window.addEventListener('variant-image', (e) => {
            if (e.detail && e.detail.id != null) {
              this.variantId = e.detail.id;
              this.sent = false; this.error = '';
            }
          });
          // The size matrix opens this shared panel for a specific out-of-stock size.
          window.addEventListener('bis:open', (e) => {
            if (e.detail && e.detail.variant) {
              this.variantId = e.detail.variant; this.label = e.detail.label || '';
              this.sent = false; this.error = '';
            }
            this.open = true;
            this.$nextTick(() => { var el = this.$root.querySelector('input[type=email]'); if (el) el.focus(); });
          });
          // Deep link from an out-of-stock product card (#notify) opens the panel on arrival.
          if (window.location.hash === '#notify') {
            this.open = true;
            this.$nextTick(() => { try { this.$root.scrollIntoView({ block: 'center' }); } catch (e) {} });
          }
        },
        async submit() {
          if (this.sending) return; // guard: the 1 to 3s Klaviyo POST must not fire on every extra tap
          this.error = '';
          if (!this.email) { this.error = window.aT('bis_no_email'); return; }
          // analytics hook regardless of provider
          document.dispatchEvent(new CustomEvent('klaviyo:back-in-stock', { detail: { email: this.email, variant: this.variantId } }));
          var company = window.SG_KLAVIYO || '';
          if (!company) {
            // Do not claim a subscription that did not happen: no company id => tell the truth.
            this.error = window.aT('bis_not_setup');
            return;
          }
          this.sending = true;
          try {
            var body = new URLSearchParams({ a: company, email: this.email, variant: String(this.variantId), platform: 'shopify' });
            var r = await fetch('https://a.klaviyo.com/onsite/components/back-in-stock/subscribe', {
              method: 'POST',
              headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
              body: body
            });
            if (!r.ok) throw new Error('subscribe failed');
            this.sent = true;
          } catch (e) { this.error = window.aT('bis_error'); }
          finally { this.sending = false; }
        }
      };
    });
  });
})();
